/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { GeminiCardsSectionFragment } from "@cosmos/hygraph-schema";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";

import {
	BackgroundContainer,
	CosmosTextVariantEnum,
	hasContent,
	LogoTitleDescButtonStack,
	ResponsiveContainer,
	SectingHeadingSizesEnum,
	SectionContainer,
} from "@components/elements";
import { HeadingLevelContext } from "@components/context";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";
import { SystemStyleObject } from "@panda/types";

import { GeminiCards } from "./GeminiCards.molecule";

const styles: SystemStyleObject = {
	"&&": {
		smDown: {
			rowGap: "eds.dimension.40",
		},
	},
	"& .gemini-section-heading .heading": {
		smDown: {
			textStyle: "headingXL",
		},
	},
	"& .logo img": {
		height: "32px",
		width: "auto",
	},
};

export const GeminiCardsSection = ({
	config,
	title,
	desc,
	cards,
	logoImage,
	cardsVariant,
	gradient,
	sectionIndex,
	hasHero,
}: GeminiCardsSectionFragment & { sectionIndex?: number; hasHero?: boolean }): React.JSX.Element | null => {
	const validHeading = Boolean(title || desc || logoImage);
	const validContent = Boolean(cards?.length);
	if (!validHeading && !validContent) return null;

	// When this is the first ref-section on a hero-less page, its title is the page h1
	// (cards then shift h3→h2); otherwise the title is an h2 nested under the hero/page h1.
	const isPageTitle = sectionIndex === 0 && !hasHero;
	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)}>
					<VStack alignItems="stretch" gap="eds.dimension.48" className={cx("gemini-card-section", css(styles))}>
						{validHeading ? (
							<LogoTitleDescButtonStack
								animate={false}
								stack={{ gap: "eds.dimension.24" }}
								image={logoImage}
								titleDescStack={{
									stack: { gap: "eds.dimension.24", align: "start", maxWidth: SectingHeadingSizesEnum.md },
									// Top-level ref-section title: the page h1 when it's the first hero-less section,
									// else h2 nested under the hero/page h1 (its cards render one below it). Visual
									// size is variant-driven (heading2XL), unchanged.
									title: { ...title, variant: "heading2XL", as: isPageTitle ? "h1" : "h2" },
									gradient,
									description: {
										html: desc,
										variant: CosmosTextVariantEnum.paragraphMD,
										as: "div",
										color: "eds.text.secondary",
									},
									className: "gemini-section-heading",
								}}
							/>
						) : null}
						{validContent ? (
							// Cards render h3 (group-heading). Shift up to h2 when the section title is the
							// page h1, or when there's no title, so the outline never skips (h1/…→h3).
							<HeadingLevelContext.Provider value={{ offset: isPageTitle ? -1 : hasContent(title) ? 0 : -1 }}>
								<GeminiCards cards={cards || []} cardsVariant={cardsVariant} />
							</HeadingLevelContext.Provider>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
